import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  FormControl,
  FormGroup,
  Typography,
  Grid,
  MenuItem,
  Select,
  InputLabel,
} from '@material-ui/core';
import React from 'react';
import { debounce } from 'lodash';

const months = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Septiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

const currentYear = new Date().getFullYear();

const DayItem = React.memo(function DayItem({
  year,
  monthIndex,
  day,
  selected,
  onToggle,
}) {
  return (
    <Box
      onClick={() => onToggle(monthIndex, day)}
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        border: '2px solid #1976d2',
        backgroundColor: selected ? '#1976d2' : 'white',
        color: selected ? 'white' : '#1976d2',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        fontSize: '0.9rem',
        margin: '4px',
      }}
    >
      {day}
    </Box>
  );
});

const TyearlyFilterOptions = ({ varState, setVarState }) => {
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [yearlyData, setYearlyData] = useState({});

  useEffect(() => {
    if (varState?.a?.d) {
      const parsed = parseMultiYearDays(varState.a.d);
      setYearlyData(parsed);

      const lastYear = Object.keys(parsed).slice(-1)[0];
      if (lastYear) setSelectedYear(parseInt(lastYear));
    }
  }, []);

  useEffect(() => {
    const debounced = debounce(() => {
      const result = formatMultiYearDays(yearlyData);
      setVarState(prev => ({
        ...prev,
        a: {
          ...prev.a,
          d: result,
        },
      }));
    }, 50);

    debounced();
    return () => debounced.cancel();
  }, [yearlyData]);

  const isLeapYear = year =>
    (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

  const getDaysInMonth = (year, monthIndex) => {
    if (monthIndex === 1) return isLeapYear(year) ? 29 : 28;
    return [3, 5, 8, 10].includes(monthIndex) ? 30 : 31;
  };

  const handleToggleDay = (monthIndex, day) => {
    setYearlyData(prev => {
      const currentYearMonths = { ...(prev[selectedYear] || {}) };
      const currentDays = currentYearMonths[monthIndex] || [];
      const exists = currentDays.includes(day);

      return {
        ...prev,
        [selectedYear]: {
          ...currentYearMonths,
          [monthIndex]: exists
            ? currentDays.filter(d => d !== day)
            : [...currentDays, day],
        },
      };
    });
  };

  const handleYearChange = e => {
    setSelectedYear(e.target.value);
  };

  const getMonthDayMap = () => yearlyData[selectedYear] || {};

  const daysByMonth = useMemo(() => {
    return months.map((_, i) =>
      Array.from(
        { length: getDaysInMonth(selectedYear, i) },
        (_, idx) => idx + 1
      )
    );
  }, [selectedYear]);

  const formatMultiYearDays = data => {
    return Object.entries(data)
      .map(([year, months]) => {
        const parts = Object.entries(months)
          .filter(([, days]) => days.length > 0)
          .map(([month, days]) => {
            const sorted = [...days].sort((a, b) => a - b);
            const ranges = [];
            let start = sorted[0];

            for (let i = 1; i <= sorted.length; i++) {
              if (sorted[i] !== sorted[i - 1] + 1) {
                const end = sorted[i - 1];
                ranges.push(start === end ? `${start}` : `${start}-${end}`);
                start = sorted[i];
              }
            }

            return `${parseInt(month) + 1}(${ranges.join(',')})`;
          });

        return `${year}[${parts.join('')}]`;
      })
      .join('');
  };

  const parseMultiYearDays = str => {
    const result = {};
    const regex = /(\d+)\[([^\]]*)\]/g;
    let match;

    while ((match = regex.exec(str)) !== null) {
      const year = parseInt(match[1], 10);
      const content = match[2];
      const monthDayMap = {};
      const monthRegex = /(\d+)\(([^)]*)\)/g;
      let mMatch;

      while ((mMatch = monthRegex.exec(content)) !== null) {
        const month = parseInt(mMatch[1], 10) - 1;
        const daysStr = mMatch[2];
        let days = [];

        daysStr.split(',').forEach(part => {
          if (part.includes('-')) {
            const [start, end] = part.split('-').map(Number);
            for (let i = start; i <= end; i++) days.push(i);
          } else if (part) {
            days.push(Number(part));
          }
        });

        monthDayMap[month] = days;
      }

      result[year] = monthDayMap;
    }

    return result;
  };

  return (
    <Box mt={2}>
      <Box mb={3} display="flex" alignItems="center" gap={2}>
        <InputLabel id="year-select-label" style={{ marginRight: 10 }}>
          Año:
        </InputLabel>

        <Select
          labelId="year-select-label"
          value={selectedYear}
          onChange={handleYearChange}
          variant="outlined"
          size="small"
        >
          {Array.from({ length: 10 }, (_, i) => currentYear - 1 + i).map(
            year => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            )
          )}
        </Select>
      </Box>

      <Box paddingLeft={6} paddingRight={6} mt={6}>
        <Grid container spacing={6} alignItems="stretch">
          {months.map((mes, monthIndex) => (
            <Grid
              item
              xs={12}
              sm={12}
              md={6}
              lg={6}
              key={monthIndex}
              style={{ display: 'flex' }}
            >
              <Box
                border={1}
                borderColor="grey.300"
                borderRadius={2}
                p={2}
                style={{ height: '100%', width: '100%' }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  {mes}
                </Typography>

                <FormControl component="fieldset">
                  <FormGroup row>
                    {daysByMonth[monthIndex].map(day => (
                      <DayItem
                        key={day}
                        year={selectedYear}
                        monthIndex={monthIndex}
                        day={day}
                        selected={
                          getMonthDayMap()[monthIndex]?.includes(day) || false
                        }
                        onToggle={handleToggleDay}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  );
};

export default TyearlyFilterOptions;
